import { Tooltip } from "antd";
import React, {
  memo,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  useCallback,
} from "react";
import * as Sentry from '@sentry/react';
import { Howl } from "howler";
import ipadSingleMp3 from "utils/assets/audio/ipad_single.mp3";
import SentryWrap from 'components/Sentry';

const rand = (m, M) => Math.random() * (M - m) + m;

const WheelComponent = ({
  segments,
  onFinished,
  dimensions,
  loader,
  cooldown,
  debateInfo,
  disableAutoSpin = false,
  autoSpinIntervalOverride = null,
  setIsWheelSpinning,
  remainingTime,
  intervalSpinNonce = 0,
  playAutoSpin = () => null,
}) => {
  const configTimer = debateInfo?.timer_auto_spin ?? debateInfo?.configurations?.timer_auto_spin;
  const timerAutoSpin = autoSpinIntervalOverride != null
    ? (autoSpinIntervalOverride > 0 ? autoSpinIntervalOverride : null)
    : (configTimer != null && configTimer > 0 ? configTimer : null);

  const cooldownRef = useRef(cooldown);
  cooldownRef.current = cooldown;
  const lastAutoSpinTimeRef = useRef(0);
  const prevRemainingTimeRef = useRef(remainingTime);

  const isIPad = useMemo(() => {
    const userAgent = navigator.userAgent;
    const platform = navigator.platform;
    const maxTouchPoints = navigator.maxTouchPoints;
    
    return (
      /iPad|Macintosh/i.test(userAgent) && 'ontouchend' in document ||
      platform === 'iPad' ||
      (maxTouchPoints && maxTouchPoints > 2 && /Macintosh/i.test(userAgent))
    );
  }, []);
  
  const isIpadOnly = !!isIPad;
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  
  const [imageUrl, setImageUrl] = useState("");
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState(0);
  const [audioReady, setAudioReady] = useState(false);
  
  const totalImages = useMemo(() => 
    segments.filter(s => s.imageUrl).length, 
    [segments]
  );

  // Use refs for Howl audio instances
  const ipadSingleAudioRef = useRef(null);

  // Performance optimization: Use refs for frequently accessed values
  const animationRef = useRef({
    angVelMax: 0,
    angVel: 0,
    ang: 0,
    isSpinning: false,
    isAccelerating: false,
    animFrame: null,
    lastFrameTime: 0,
    targetFPS: isIpadOnly ? 60 : 60, // Consistent FPS across devices
    frameInterval: isIpadOnly ? 1000 / 60 : 1000 / 60,
    audioStarted: false // Track if audio has started for this spin
  });

  const lastInteractionRef = useRef({
    time: 0,
    type: null
  });


  // Initialize audio files using Howl with refs
  useEffect(() => {
    const initializeAudio = () => {
      try {
        // Initialize iPad single audio for all devices
        if (!ipadSingleAudioRef.current) {
          ipadSingleAudioRef.current = new Howl({
            src: [ipadSingleMp3],
            volume: 1,
            html5: isIPad ? true : false, // Use Web Audio API for better performance
            preload: true,
            onload: () => {
              console.log('Audio loaded successfully');
            },
            onloaderror: function(id, err) {
              console.warn('Audio failed to load:', err);
              // Fallback to HTML5 audio
              if (ipadSingleAudioRef.current) {
                ipadSingleAudioRef.current.unload();
                ipadSingleAudioRef.current = new Howl({
                  src: [ipadSingleMp3],
                  volume: 1,
                  html5: true,
                  preload: true
                });
              }
            }
          });
        }

        // Set audio as ready after initialization
        setAudioReady(true);
        console.log('Bingo wheel audio initialized successfully');
      } catch (err) {
        Sentry.captureException(`Audio initialization error: ${err}`);
        console.error('BINGO_WHEEL: failed to initialize audio', err);
      }
    };

    initializeAudio();

    // Cleanup function
    return () => {
      if (ipadSingleAudioRef.current) {
        ipadSingleAudioRef.current.unload();
      }
    };
  }, []);
  
  const unlockAudio = async () => {
    if (audioReady) return;
    
    try {
      // Try to unlock audio by playing and immediately stopping
      const ipadSingleAudio = ipadSingleAudioRef.current;
      
      if (ipadSingleAudio) {
        const audioPromise = ipadSingleAudio.play();
        if (audioPromise) {
          await audioPromise;
          ipadSingleAudio.stop();
        }
      }
      
      console.log('Audio unlocked successfully');
    } catch (e) {
      console.warn('Audio unlock failed:', e.message);
    }
  };
  
  const [, forceUpdate] = useReducer((x) => x + 1, 0);
  const tot = segments.length;
  const canvasRef = useRef(null);
  const [ctx, setCtx] = useState(null);
  const [loadedImages, setLoadedImages] = useState({});
  
  // Optimized index calculation with better precision
  const getIndex = useCallback(() => {
    const anim = animationRef.current;
    const normalizedAngle = anim.ang % (2 * Math.PI);
    const positiveAngle = normalizedAngle < 0 ? normalizedAngle + 2 * Math.PI : normalizedAngle;
    const sectorIndex = Math.floor(tot - (positiveAngle / (2 * Math.PI)) * tot) % tot;
    
    // Ensure index is within bounds
    return Math.max(0, Math.min(sectorIndex, tot - 1));
  }, [tot]);

  // Basic wheel setup with better constants
  const wheelConfig = useMemo(() => {
    const dia = 400;
    const rad = dia / 2;
    const PI = Math.PI;
    const TAU = 2 * PI;
    const arc = TAU / tot;
    
    // Optimized physics constants for 2.3 second spin duration
    const friction = isIpadOnly ? 0.88 : 0.87;         // Faster deceleration for shorter duration
    const angVelMin = isIpadOnly ? 0.008 : 0.008;      // Higher minimum velocity for faster stopping
    const accelerationMultiplier = isIpadOnly ? 1.12 : 1.10; // Faster acceleration for quicker start
    
    return {
      dia,
      rad,
      PI,
      TAU,
      arc,
      friction,
      angVelMin,
      accelerationMultiplier
    };
  }, [tot, isIpadOnly]);

  // Safe audio play function using Howl
  const safePlayAudio = async (audioRef) => {
    if (!audioRef?.current || !audioReady) return;
    
    try {
      audioRef.current.play();
    } catch (error) {
      console.warn('Audio play failed:', error.message);
    }
  };

  // Safe audio pause function using Howl
  const safePauseAudio = (audioRef) => {
    if (!audioRef?.current) return;
    
    try {
      audioRef.current.stop();
    } catch (error) {
      console.warn('Audio pause failed:', error.message);
    }
  };

  // Load single image with better error handling
  const loadSingleImage = async (imageUrl) => {
    if (!imageUrl) return null;
    if (loadedImages[imageUrl]) return loadedImages[imageUrl];

    try {
      const img = new Image();
      
      await new Promise((resolve, reject) => {
        img.onload = () => {
          setLoadedImages(prev => ({
            ...prev,
            [imageUrl]: img,
          }));
          setImagesLoaded(prev => Math.min(prev + 1, totalImages));
          resolve(img);
        };
        
        img.onerror = () => {
          console.error(`Failed to load image: ${imageUrl}`);
          setImagesLoaded(prev => Math.min(prev + 1, totalImages));
          reject(new Error(`Failed to load image: ${imageUrl}`));
        };

        img.src = imageUrl;
      });

      return img;
    } catch (error) {
      Sentry.captureException(`Error in loadSingleImage: ${error}`);
      console.error(error);
      return null;
    }
  };

  // Initialize canvas context
  useEffect(() => {
    if (canvasRef.current) {
      const context = canvasRef.current.getContext("2d", {
        alpha: true, // Enable alpha for transparency
        antialias: false, // Better performance on mobile
        powerPreference: "high-performance" // Request high performance
      });
      setCtx(context);
    }
  }, [canvasRef]);

  // Load images when segments change
  useEffect(() => {
    const loadImages = async () => {
      const imagesToLoad = segments.filter(s => s.imageUrl && !loadedImages[s.imageUrl]);
      
      if (imagesToLoad.length === 0) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setImagesLoaded(0);

      try {
        await Promise.all(
          imagesToLoad?.map(segment => loadSingleImage(segment.imageUrl))
        );
      } catch (error) {
        Sentry.captureException(`Error in loadImages: ${error}`);
        console.error("Error loading images:", error);
      }
    };

    loadImages();
  }, [segments]);

  // Handle loading completion and draw wheel
  useEffect(() => {
    if (imagesLoaded === totalImages) {
      setIsLoading(false);
      if (ctx) {
        clearCanvas();
        segments.forEach(drawSector);
      }
    }
  }, [imagesLoaded, totalImages, ctx, debateInfo]);

  const drawLoadingState = () => {
    if (!ctx) return;
    
    clearCanvas();
    
    // Draw transparent loading background
    ctx.fillStyle = 'rgba(240, 240, 240, 0.8)';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    
    // Draw loading text
    ctx.fillStyle = '#333';
    ctx.font = 'bold 24px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    
    // Calculate loading percentage
    const loadingPercent = totalImages === 0 ? 100 : Math.round((imagesLoaded / totalImages) * 100);
    ctx.fillText(`Loading... ${loadingPercent}%`, wheelConfig.rad, wheelConfig.rad - 30);
    
    // Draw loading bar
    const barWidth = 200;
    const barHeight = 10;
    const barX = wheelConfig.rad - barWidth / 2;
    const barY = wheelConfig.rad + 10;
    
    // Draw bar background
    ctx.fillStyle = 'rgba(221, 221, 221, 0.8)';
    ctx.fillRect(barX, barY, barWidth, barHeight);
    
    // Draw progress
    ctx.fillStyle = '#4CAF50';
    ctx.fillRect(barX, barY, barWidth * (loadingPercent / 100), barHeight);
  };

  // Draw loading state when loading
  useEffect(() => {
    if (isLoading && ctx) {
      drawLoadingState();
    }
  }, [isLoading, imagesLoaded, ctx]);

  useLayoutEffect(() => {
    if (!isLoading && ctx && segments?.length) {
      clearCanvas();
      segments.forEach(drawSector);
    }
  }, [segments, ctx, loadedImages, isLoading, debateInfo]);

  const clearCanvas = () => {
    if (ctx) {
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      // Clear with transparency instead of black
      ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      ctx.restore();
    }
  };

  const truncateText = (text, length = 10) => {
    return text.length > length ? text.slice(0, length) + "..." : text;
  };

  const drawSector = (sector, i) => {
    if (ctx) {
      const ang = wheelConfig.arc * i;
      ctx.save();

      // Draw sector background with transparency support
      ctx.beginPath();
      ctx.fillStyle = sector.color;
      ctx.moveTo(wheelConfig.rad, wheelConfig.rad);
      ctx.arc(wheelConfig.rad, wheelConfig.rad, wheelConfig.rad, ang, ang + wheelConfig.arc);
      ctx.lineTo(wheelConfig.rad, wheelConfig.rad);
      ctx.fill();

      // Draw text and image
      ctx.translate(wheelConfig.rad, wheelConfig.rad);
      ctx.rotate(ang + wheelConfig.arc / 2);

      // Calculate positions based on segment count and image presence
      const hasValidImage =
        sector.imageUrl &&
        loadedImages[sector.imageUrl] &&
        loadedImages[sector.imageUrl] !== "";

      // Adjust text positioning based on segment count
      const isHighDensity = segments?.length > 30;
      
      // Calculate available space for text
      const imageSpace = isHighDensity ? 15 : 35;
      const imageMargin = isHighDensity ? 5 : 15;
      const textMargin = isHighDensity ? 10 : 20;
      
      // Calculate text position and max width
      const textPosition = hasValidImage 
        ? wheelConfig.rad - (imageSpace + imageMargin)
        : wheelConfig.rad - textMargin;
      
      const maxWidth = hasValidImage
        ? wheelConfig.rad - (imageSpace + imageMargin + textMargin)
        : wheelConfig.rad - (textMargin * 2);

      // Adjust font size based on segment count
      const fontSize = isHighDensity ? 10 : 12;
      ctx.font = `bold ${fontSize}px sans-serif`;
      ctx.textAlign = "right";
      ctx.fillStyle = "#fff";

      // Handle text wrapping and truncation
      const label = debateInfo?.bingo_type === 'OnlyImage' ? '' : (sector?.label ?? "");
      // Measure and truncate text to fit
      const marginLeft = 2;
      const measureText = ctx.measureText(label);
      if (measureText.width > maxWidth || label.length > 10) {
        // Calculate how many characters will fit
        let fitLength = 0;
        let testString = '';
        for (let i = 0; i < label.length; i++) {
          testString += label[i];
          if (ctx.measureText(testString + '...').width > maxWidth) {
            break;
          }
          fitLength = i + 1;
        }
        const truncatedText = truncateText(label)
        ctx.fillText(truncatedText, textPosition + marginLeft , 0, maxWidth);
      } else {
        ctx.fillText(label, textPosition + marginLeft, 0, maxWidth);
      }

      // Draw image
      if (hasValidImage) {
        try {
          const img = loadedImages[sector.imageUrl];
          const imgSize = isHighDensity ? 10 : 30;
          const imgX = wheelConfig.rad - (isHighDensity ? 15 : 35);
          const imgY = isHighDensity ? -5 : -15;
          ctx.drawImage(img, imgX, imgY, imgSize, imgSize);
        } catch (err) {
          Sentry.captureException(`Error in drawSector: ${err}`);
          console.error("Error drawing image:", err);
        }
      }

      ctx.restore();
    }
    forceUpdate();
  };

  // Optimized rotation function with better performance
  const rotate = useCallback(() => {
    if (ctx) {
      const anim = animationRef.current;
      // Use transform3d for hardware acceleration on mobile devices
      const transform = isTouchDevice ? 
        `rotate3d(0, 0, 1, ${anim.ang - wheelConfig.PI / 2}rad)` :
        `rotate(${anim.ang - wheelConfig.PI / 2}rad)`;
      
      ctx.canvas.style.transform = transform;
      // Force hardware acceleration
      ctx.canvas.style.willChange = 'transform';
    }
  }, [ctx, isTouchDevice, wheelConfig.PI]);

  // Optimized frame function with consistent timing and better iPad audio handling
  const frame = useCallback((currentTime) => {
    const anim = animationRef.current;
    
    if (!anim.isSpinning) return;
    
    // Frame rate limiting for consistent performance
    if (currentTime - anim.lastFrameTime < anim.frameInterval) {
      anim.animFrame = requestAnimationFrame(frame);
      return;
    }
    
    anim.lastFrameTime = currentTime;
    
    if (anim.angVel >= anim.angVelMax) {
      anim.isAccelerating = false;
    }
    
    const runningAudio = ipadSingleAudioRef.current;
    
    // Only play audio once when starting to spin
    if (runningAudio && audioReady && !anim.audioStarted) {
      if (!runningAudio.playing()) {
        runningAudio.play();
        anim.audioStarted = true; // Mark audio as started
      }
    }

    if (anim.isAccelerating) {
      anim.angVel = anim.angVel || wheelConfig.angVelMin;
      anim.angVel *= wheelConfig.accelerationMultiplier;
    } else {
      anim.angVel *= wheelConfig.friction;
      
      // Optimized stopping threshold for 2.3 second duration
      const stopThreshold = wheelConfig.angVelMin * 1.05;
      if (anim.angVel < stopThreshold) {
        anim.isSpinning = false;
        if (typeof setIsWheelSpinning === 'function') {
          setIsWheelSpinning(false);
        }
        anim.angVel = 0;
        cancelAnimationFrame(anim.animFrame);
        
        // Stop the audio immediately when wheel stops
        if (runningAudio && runningAudio.playing()) {
          safePauseAudio(ipadSingleAudioRef);
        }
        
        // Calculate final position with consistent algorithm
        const finalAngle = anim.ang % wheelConfig.TAU;
        const normalizedAngle = finalAngle < 0 ? finalAngle + wheelConfig.TAU : finalAngle;
        const sectorIndex = Math.floor(tot - (normalizedAngle / wheelConfig.TAU) * tot) % tot;
        const sector = segments[sectorIndex];
        

        
        // Reset audio flag for next spin
        anim.audioStarted = false;
        
        // Show result immediately without delay
        setResult(sector?.label ?? "");
        setImageUrl(sector?.imageUrl || "");
        
        onFinished(sector?.label ?? "");
        
        return;
      }
    }

    anim.ang += anim.angVel;
    anim.ang %= wheelConfig.TAU;
    rotate();
    
    anim.animFrame = requestAnimationFrame(frame);
  }, [audioReady, rotate, segments, tot, wheelConfig, onFinished]);

  const engine = useCallback(() => {
    animationRef.current.lastFrameTime = performance.now();
    animationRef.current.animFrame = requestAnimationFrame(frame);
  }, [frame]);

  const drawer = async () => {
    playAutoSpin(true);
    const anim = animationRef.current;
    if (anim.isSpinning || loader || cooldown || isLoading || segments?.length === 0) {
      return;
    }
    const hasVisibleResult =
      Boolean(imageUrl) ||
      (result !== null &&
        result !== undefined &&
        String(result) !== "");
    if (hasVisibleResult) {
      setResult(null);
      setImageUrl("");
    }

    anim.isSpinning = true;
    if (typeof setIsWheelSpinning === 'function') {
      setIsWheelSpinning(true);
    }
    anim.isAccelerating = true;
    anim.audioStarted = false; // Reset audio flag for new spin
    
    // Optimized velocity ranges for 2.3 second spin duration
    anim.angVelMax = isIpadOnly ? rand(0.35, 0.40) : rand(0.33, 0.38);

    // Start the wheel animation immediately
    engine();
    
    // Audio will be started in the frame function to ensure synchronization
  };

  const unifiedInteractionHandler = useCallback(async (event, interactionType) => {
    if (event.cancelable) {
      event.preventDefault();
    }
    event.stopPropagation();
    
    const anim = animationRef.current;
    const now = Date.now();
    const lastInteraction = lastInteractionRef.current;
    
    // If spinning or loading, don't allow clicks
    if (anim.isSpinning || loader || isLoading || cooldown  || isLoading) {
      return;
    }
    
    
    if (lastInteraction.type && 
        lastInteraction.type !== interactionType && 
        now - lastInteraction.time < 300) {
      return;
    }
    
    lastInteractionRef.current = {
      time: now,
      type: interactionType
    };
    
    if (!audioReady) {
      await unlockAudio();
    }
    
    await drawer();
  }, [result, imageUrl, loader, cooldown, isLoading, audioReady]);

  const handleTouchDrawer = useCallback(async (event) => {
    await unifiedInteractionHandler(event, 'touch');
  }, [unifiedInteractionHandler]);

  const handleMouseClick = useCallback(async (event) => {
    await unifiedInteractionHandler(event, 'mouse');
  }, [unifiedInteractionHandler]);

  useEffect(() => {
    prevRemainingTimeRef.current = remainingTime;
  }, [timerAutoSpin]);

  useEffect(() => {
    if (!intervalSpinNonce || disableAutoSpin) {
      return;
    }
    if (timerAutoSpin == null || timerAutoSpin <= 0 || !segments?.length) {
      return;
    }
    if (animationRef.current.isSpinning) {
      return;
    }
    lastAutoSpinTimeRef.current = Date.now();
    const syntheticEvent = {
      cancelable: false,
      preventDefault: () => {},
      stopPropagation: () => {}
    };
    const tid = setTimeout(() => {
      handleMouseClick(syntheticEvent);
    }, 0);
    return () => clearTimeout(tid);
  }, [intervalSpinNonce, timerAutoSpin, handleMouseClick, segments?.length, disableAutoSpin]);

  useEffect(() => {
    if (timerAutoSpin == null || timerAutoSpin <= 0 || !segments?.length || disableAutoSpin) return;
    lastAutoSpinTimeRef.current = Date.now();
    const syntheticEvent = {
      cancelable: false,
      preventDefault: () => {},
      stopPropagation: () => {}
    };
    const checkIntervalMs = 500;
    const intervalId = setInterval(() => {
      const now = Date.now();
      const elapsedSinceLastSpin = (now - lastAutoSpinTimeRef.current) / 1000;
      if (
        elapsedSinceLastSpin >= timerAutoSpin &&
        !cooldownRef.current &&
        segments?.length > 0 &&
        !disableAutoSpin
      ) {
        lastAutoSpinTimeRef.current = now;
        handleMouseClick(syntheticEvent);
      }
    }, checkIntervalMs);
    return () => clearInterval(intervalId);
  }, [timerAutoSpin, handleMouseClick, segments?.length, disableAutoSpin]);

  useEffect(() => {
    if (
      timerAutoSpin != null &&
      timerAutoSpin > 0 &&
      segments?.length > 0 &&
      !disableAutoSpin &&
      prevRemainingTimeRef.current > 0 &&
      remainingTime === 0 &&
      !cooldownRef.current
    ) {
      lastAutoSpinTimeRef.current = Date.now();
      const syntheticEvent = {
        cancelable: false,
        preventDefault: () => {},
        stopPropagation: () => {}
      };
      handleMouseClick(syntheticEvent);
    }
    prevRemainingTimeRef.current = remainingTime;
  }, [remainingTime, timerAutoSpin, handleMouseClick, segments?.length, disableAutoSpin]);

  useLayoutEffect(() => {
    clearCanvas();
    if (segments && segments.length) {
      segments.forEach(drawSector);
    }
  }, [segments, ctx, loadedImages, debateInfo]);

  const getCalculatedHeight = () => {
    if (300 < dimensions.height && dimensions.height < 791) {
      return dimensions?.height - 150;
    } else if (791 < dimensions.height && dimensions.height < 1529) {
      return dimensions?.height - 300;
    } else if (dimensions.height > 1647) {
      return 1317;
    }
  };

  const handleHideResult = () => {
    const anim = animationRef.current;
    if (anim.isSpinning || loader || cooldown) return;
    setResult(null);
    setImageUrl("");
  };

  return (
    <SentryWrap>
    <div
      id="newWheelOfFortune"
      onClick={handleMouseClick}
      onTouchStart={handleTouchDrawer}
      style={{ 
        position: 'relative',
        pointerEvents: animationRef.current.isSpinning ? 'none' : 'auto',
        touchAction: 'manipulation', // Optimize touch handling
        WebkitUserSelect: 'none', // Prevent text selection on iOS
        userSelect: 'none'
      }}
    >
      {/* Audio loading indicator */}
      {!audioReady && (
        <div style={{
          position: 'absolute',
          top: '10px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10,
          textAlign: 'center',
          padding: '8px 16px',
          backgroundColor: 'rgba(240, 240, 240, 0.9)',
          borderRadius: '20px',
          fontSize: '12px',
          color: '#666',
          border: '1px solid rgba(221, 221, 221, 0.8)',
          backdropFilter: 'blur(10px)'
        }}>
          🔊 Loading audio...
        </div>
      )}
      
      {(Boolean(imageUrl) ||
        (result !== null &&
          result !== undefined &&
          String(result) !== "")) ? (
        <div
          style={{ width: "100%", height: getCalculatedHeight() }}
          id="newWheelOfFortuneResult"
          onClick={handleHideResult}
        >
          <div className="flex flex-col items-center cursor-pointer bingoNewWheel">
            {imageUrl && (
              <img
                src={imageUrl}
                alt=""
                style={{
                  width: "100%",
                  height: "193px",
                  maxWidth: "300px",
                  objectFit: "contain",
                }}
              />
            )}
            {debateInfo?.bingo_type !== 'OnlyImage' && (
              <div style={{ fontSize: "32px" }} id="newWheelOfFortuneResultText">
                <Tooltip placement="top" title={String(result ?? "")}>
                  {String(result ?? "").length > 15
                    ? `${String(result ?? "").slice(0, 15)}...`
                    : String(result ?? "")}
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ) : null}
      <canvas
        onClick={handleMouseClick}
        onTouchStart={handleTouchDrawer}
        style={{ 
          height: getCalculatedHeight(),
          cursor: isLoading ? 'wait' : animationRef.current.isSpinning ? 'not-allowed' : 'pointer',
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
          backgroundColor: 'transparent',
          pointerEvents: animationRef.current.isSpinning ? 'none' : 'auto'
        }}
        ref={canvasRef}
        id="newWheel"
        width="400"
        height="400"
      />
      <div 
        id="newSpin" 
        onClick={handleMouseClick}
        onTouchStart={handleTouchDrawer}
        style={{ 
          opacity: isLoading || animationRef.current.isSpinning ? 0.5 : 1,
          cursor: isLoading ? 'wait' : animationRef.current.isSpinning ? 'not-allowed' : 'pointer',
          touchAction: 'manipulation',
          pointerEvents: animationRef.current.isSpinning ? 'none' : 'auto'
        }}
      >
        {isLoading ? 'LOADING...' : animationRef.current.isSpinning ? 'SPIN' : 'SPIN'}
      </div>
    </div>

  
    {segments?.length === 0 && (
      <div className="flex flex-col items-center justify-center h-full">
        <div className="text-base font-bold text-red-10">No Data Available</div>
      </div>
    )}
    </SentryWrap>
  );
};

export default memo(WheelComponent);